import { FunctionComponent, useState } from 'react';
import { Box, Drawer, SvgIcon, Typography } from '@mui/material';
import { StarjackTable } from '@/components/Table/v2/Table';
import { Mail } from '@/models/mail';
import { formatStarjackDateWithTime } from '@/utils/time';
import IconButton from '@mui/material/IconButton';
import { EnvelopeOpenIcon } from '@heroicons/react/24/solid';
import { MailDrawerContent } from '@/pageContents/mails/MailDrawerContent';
import { usePagination } from '@starjack/shared-components';
import { useGetMailsQuery } from '@/api/v2/mails';

type OrderDetailsMailsTableProps = {
    orderId: string;
};

export const OrderDetailsMailsTable: FunctionComponent<
    OrderDetailsMailsTableProps
> = ({ orderId }) => {
    const { data: mails = [], error } = useGetMailsQuery({
        orderId: orderId,
    });

    const {
        currentItems: currentMails,
        itemsPerPage: mailsPerPage,
        setNextPage: setMailsPage,
        setItemsPerPage,
        page,
        countTotal,
    } = usePagination({
        items: mails,
        itemsPerPage: 10,
    });

    const [mailDetailConfig, setMailDetailConfig] = useState<{
        isOpen: boolean;
        mail: Mail;
    }>({
        isOpen: false,
        mail: null,
    });

    const onCloseDrawer = () => {
        setMailDetailConfig((prev) => ({ ...prev, isOpen: false }));
    };

    return (
        <>
            <Box>
                <Typography
                    sx={{
                        mb: 4,
                    }}
                    variant='h5'
                >
                    Mails
                </Typography>

                <StarjackTable<Mail>
                    allItems={mails || []}
                    count={countTotal}
                    currentPage={page}
                    items={currentMails}
                    error={error}
                    onPageChange={setMailsPage}
                    rowsPerPage={mailsPerPage}
                    onRowsPerPageChange={setItemsPerPage}
                    itemId='id'
                    columns={[
                        {
                            label: 'Gesendet am',
                            itemTransform: (mail) =>
                                formatStarjackDateWithTime(mail.sentAt),
                        },
                        {
                            label: 'Betreffe',
                            itemKey: 'subject',
                        },
                        {
                            label: 'Mail öffnen',
                            itemTransform: (mail) => (
                                <IconButton
                                    aria-label='Mail öffnen'
                                    onClick={() => {
                                        setMailDetailConfig({
                                            isOpen: true,
                                            mail,
                                        });
                                    }}
                                >
                                    <SvgIcon fontSize='medium'>
                                        <EnvelopeOpenIcon />
                                    </SvgIcon>
                                </IconButton>
                            ),
                            options: {
                                headerCellProps: {
                                    align: 'center',
                                },
                                rowCellProps: {
                                    align: 'center',
                                },
                            },
                        },
                    ]}
                />
            </Box>

            <Drawer
                anchor='right'
                open={!!mailDetailConfig.isOpen}
                onClose={onCloseDrawer}
            >
                <MailDrawerContent
                    mail={mailDetailConfig.mail}
                    onClose={onCloseDrawer}
                />
            </Drawer>
        </>
    );
};
